import React, { useState, useRef, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { TimeZoneContext } from '@contexts/timezone-context';
import { Dialog } from '@progress/kendo-react-dialogs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { DateTimePicker } from '@progress/kendo-react-dateinputs';
import { convertLocalDateToUTC } from '@utils/dateUtils';
import I18n, { translate } from '@utils/i18n';
import api from '@utils/axios';
import { KendoIntlProvider } from '@contexts/kendo-intl-context';
import { Button } from '@ui';
import { massUpdateInspectionsPath } from 'routes';
import TooltipText from '@components/v2/TooltipText';

const namespace = 'features.inspections_index.mass_update_modal';
const dateTimeFormat = 'MM/dd/yyyy h:mm a';
const BATCH_SIZE = 10;

function MassUpdateModal({
  inspectionIds,
  inspectionsCount,
  disabled,
  statusesForSelect,
  techniciansForSelect,
  inspectionTypesForSelect,
  queryParams,
  onDataChanged,
  inspectionSeriesId,
  hasPendingIssues
}) {
  const [visible, setVisible] = useState(false);
  const defaultTech = { label: translate('technician_placeholder', { namespace }), value: '' };
  const defaultStatus = { label: translate('status_placeholder', { namespace }), value: '' };
  const defaultInspectionType = { label: translate('inspection_type_placeholder', { namespace }), value: '' };
  const [tech, setTech] = useState(defaultTech);
  const [status, setStatus] = useState(defaultStatus);
  const [inspectionType, setInspectionType] = useState(defaultInspectionType);
  const [scheduledDate, setScheduledDate] = useState();
  const [inspectionsProcessed, setInspectionsProcessed] = useState(0);
  const [errors, setErrors] = useState([]);
  const shouldContinueRef = useRef(true);
  // Identifies the in-flight submission. Bumped on every onSubmit and on
  // clearForm/cancel so async completions from a stale/canceled submission
  // (e.g. cancel + resubmit while a request is still in flight) can detect
  // they're stale and no-op instead of touching state for the wrong run.
  const submissionIdRef = useRef(0);
  const { timezone } = useContext(TimeZoneContext);

  const toggleDialog = () => {
    setVisible(!visible);
  };

  const clearForm = () => {
    shouldContinueRef.current = false;
    submissionIdRef.current += 1;
    toggleDialog();
    setTech(defaultTech);
    setStatus(defaultStatus);
    setInspectionType(defaultInspectionType);
    setScheduledDate();
    setErrors([]);
    setInspectionsProcessed(0);
  };

  const onDropDownChange = (setState) => {
    return (event) => {
      setState(event.target.value);
    };
  };

  const fetchIdsPath = useMemo(() => `${massUpdateInspectionsPath({ all: true })}&${queryParams}`, [queryParams]);

  const onSubmit = () => {
    shouldContinueRef.current = true;
    submissionIdRef.current += 1;
    const submissionId = submissionIdRef.current;
    const isStale = () => submissionIdRef.current !== submissionId;
    setErrors([]);

    const formData = {
      technician_id: tech.value,
      status_code: status.value,
      inspection_type_id: inspectionType.value,
      scheduled_date: scheduledDate && convertLocalDateToUTC(scheduledDate, timezone),
      inspection_series_id: inspectionSeriesId
    };

    if (!inspectionIds) {
      api
        .post(fetchIdsPath)
        .then(({ data }) => {
          if (isStale()) return;

          if (data.length !== inspectionsCount) {
            setErrors([translate('count_mismatch', { namespace, expected: inspectionsCount, actual: data.length })]);
            return;
          }

          batchUpdate(formData, data, null, submissionId);
        })
        .catch(({ response }) => {
          if (isStale()) return;
          setErrors(response?.data?.errors ?? [translate('load_inspection_ids_error', { namespace })]);
        });
    } else {
      const inspectionIdsArray = inspectionIds.split(',');

      if (inspectionIdsArray.length > BATCH_SIZE) {
        batchUpdate(formData, inspectionIdsArray, null, submissionId);
      } else {
        api
          .post(massUpdateInspectionsPath(), { ...formData, ids: inspectionIds })
          .then(({ data }) => {
            if (isStale()) return;
            onDataChanged(I18n.t('inspections.mass_update.success'), data);
            clearForm();
          })
          .catch(({ response }) => {
            if (isStale()) return;
            setErrors(response.data.errors);
          });
      }
    }
  };

  const batchUpdate = (formData, ids, prevData, submissionId) => {
    if (submissionIdRef.current !== submissionId) {
      // A newer submission started, or the dialog was canceled — this batch is stale.
      return;
    }

    if (!shouldContinueRef.current) {
      console.log('Loop aborted by the user.');
      return;
    }

    if (!ids.length) {
      onDataChanged(I18n.t('inspections.mass_update.success'), prevData);
      clearForm();
      return;
    }

    api.post(massUpdateInspectionsPath(), { ...formData, ids: ids.splice(0, BATCH_SIZE) }).then(({ data }) => {
      if (submissionIdRef.current !== submissionId) return;
      setInspectionsProcessed((prev) => prev + data.ids.length);
      batchUpdate(formData, ids, data, submissionId);
    });
  };

  const statusItemRender = (element, itemProps) => {
    const { icon, color } = itemProps.dataItem;
    return React.cloneElement(element, element.props, statusRenderTemplate(element, icon, color));
  };

  const statusValueRender = (element, value) => {
    if (!value || !value.icon) {
      return element;
    }

    return React.cloneElement(element, element.props, statusRenderTemplate(element, value.icon, value.color));
  };

  const statusRenderTemplate = (element, icon, color) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span
          className="qmb-avatar--24--status-icon"
          style={{ display: 'flex', borderRadius: '50%', alignItems: 'center', justifyContent: 'center' }}>
          <i className={`fa-lg ${icon}`} style={{ color }} />
        </span>
        <div style={{ marginLeft: '0.8rem' }}>{element.props.children}</div>
      </div>
    );
  };

  return (
    <KendoIntlProvider>
      <Button tabIndex="0" disabled={disabled} onClick={clearForm}>
        <i className="fa-light fa-pen-to-square" />
        {translate('button', { namespace })}
      </Button>
      {visible && (
        <Dialog title={translate('title', { namespace })} className="qmb-dialog" onClose={toggleDialog}>
          <p>{translate('sub_title', { namespace, count: inspectionsCount })}</p>
          <table className="qmb-table--spreadsheet">
            <thead>
              <tr>
                <th>{translate('change', { namespace })}</th>
                <th>{translate('value', { namespace })}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="nested_fields">
                <td className="table__cell">
                  <div className="qmb-input--text">
                    <input value={translate('technician', { namespace })} disabled />
                  </div>
                </td>
                <td className="table__cell">
                  <div className="qmb-select--blank">
                    <DropDownList
                      name="technician_id"
                      id="techForSel"
                      textField="label"
                      dataItemKey="value"
                      data={techniciansForSelect}
                      value={tech}
                      defaultItem={defaultTech}
                      onChange={onDropDownChange(setTech)}
                      size="large"
                    />
                  </div>
                </td>
              </tr>
              <tr className="nested_fields">
                <td className="table__cell">
                  <div className="qmb-input--text">
                    <input value={translate('scheduled_date', { namespace })} disabled />
                  </div>
                </td>
                <td className="table__cell">
                  <div className="qmb-input--date--blank">
                    <DateTimePicker
                      name="scheduled_date"
                      placeholder={translate('scheduled_date_placeholder', { namespace })}
                      value={scheduledDate}
                      onChange={onDropDownChange(setScheduledDate)}
                      format={dateTimeFormat}
                    />
                  </div>
                </td>
              </tr>
              <tr className="nested_fields">
                <td className="table__cell">
                  <div className="qmb-input--text">
                    <input value={translate('inspection_type', { namespace })} disabled />
                  </div>
                </td>
                <td className="table__cell">
                  <div className="qmb-select--blank">
                    <DropDownList
                      name="inspection_type_id"
                      id="inspectionTypeForSel"
                      textField="label"
                      dataItemKey="value"
                      data={inspectionTypesForSelect}
                      value={inspectionType}
                      defaultItem={defaultInspectionType}
                      onChange={onDropDownChange(setInspectionType)}
                      size="large"
                    />
                  </div>
                </td>
              </tr>
              <tr className="nested_fields">
                <td className="table__cell">
                  <div className="qmb-input--text">
                    <input value={translate('status', { namespace })} disabled />
                  </div>
                </td>
                <td className="table__cell">
                  <div className="qmb-select--blank">
                    {!hasPendingIssues && (
                      <DropDownList
                        name="status"
                        id="statusForSel"
                        textField="label"
                        dataItemKey="value"
                        data={statusesForSelect}
                        itemRender={statusItemRender}
                        valueRender={statusValueRender}
                        value={status}
                        defaultItem={defaultStatus}
                        onChange={onDropDownChange(setStatus)}
                        size="large"
                      />
                    )}
                    {hasPendingIssues && (
                      <span className="tooltipText__container--full-width">
                        <TooltipText
                          multiline
                          text={translate('there_are_outstanding_inspection_assistant_issues', {
                            namespace: 'features.inspections_index.table'
                          })}>
                          <DropDownList
                            name="status"
                            id="statusForSel"
                            textField="label"
                            dataItemKey="value"
                            data={statusesForSelect}
                            itemRender={statusItemRender}
                            valueRender={statusValueRender}
                            value={status}
                            defaultItem={defaultStatus}
                            onChange={onDropDownChange(setStatus)}
                            size="large"
                            disabled
                          />
                        </TooltipText>
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div>
            {errors.map((error, i) => (
              <div key={i}>
                <i className="fa-solid fa-do-not-enter" style={{ color: '#DB2B39' }} />
                {error}
              </div>
            ))}
          </div>
          <div className="modal__footer pull-right" style={{ justifyContent: 'space-between' }}>
            <div className="mt-3">
              {inspectionsProcessed > 0 && (
                <div>
                  Processing: {inspectionsProcessed}/{inspectionsCount}
                </div>
              )}
              <button type="button" className="qmb-button" onClick={clearForm}>
                {I18n.t('generic.cancel')}
              </button>
              <button type="button" onClick={onSubmit} className="qmb-button--submit">
                {I18n.t('generic.update')}
              </button>
            </div>
          </div>
        </Dialog>
      )}
    </KendoIntlProvider>
  );
}

MassUpdateModal.propTypes = {
  inspectionIds: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  disabled: PropTypes.bool.isRequired,
  queryParams: PropTypes.object.isRequired,
  inspectionsCount: PropTypes.number.isRequired,
  techniciansForSelect: PropTypes.arrayOf(PropTypes.object),
  statusesForSelect: PropTypes.arrayOf(PropTypes.object),
  inspectionTypesForSelect: PropTypes.arrayOf(PropTypes.object),
  onDataChanged: PropTypes.func.isRequired,
  inspectionSeriesId: PropTypes.number,
  hasPendingIssues: PropTypes.bool
};

MassUpdateModal.defaultProps = {
  techniciansForSelect: [],
  statusesForSelect: [],
  inspectionTypesForSelect: [],
  inspectionIds: null,
  inspectionSeriesId: null,
  hasPendingIssues: false
};

export default MassUpdateModal;
